'use client'

import React from 'react'
import { AlertTriangle, BadgeCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { impersonatesVerified, isVerified } from '../lib/amm'

/// Identity marks for an alkane's NAME.
///
/// Alkane names are unconstrained and collide freely: the live pool set carries
/// four TORTILLAs, three pis, two frBTCs and two bUSDs. The id is the only
/// identity that matters, so every surface that renders a name renders these
/// beside it. They live in one file precisely so a redesign cannot drop them
/// from one screen and keep them on another.

export interface NameFlags {
	verified: boolean
	/// The id a token is claiming to be, when its name matches a canonical
	/// asset's and its id does not. `frBTC 2:62280` is not frBTC.
	impostorOf: string | null
	/// More than one live token answers to this name.
	ambiguous: boolean
}

/// Resolve the three flags for one asset against the set of names that are
/// shared by more than one id (see `ambiguousNames`).
export function nameFlags(
	asset: { id: string; name: string | null },
	ambiguousSet: Set<string>,
	additionalVerified: { id: string; name: string | null }[] = []
): NameFlags {
	const verified = isVerified(
		asset.id,
		additionalVerified.map((candidate) => candidate.id)
	)
	const impostorOf = impersonatesVerified(asset, additionalVerified)
	return {
		verified,
		impostorOf,
		// An impostor already gets the louder mark; a verified asset sharing its
		// own name with an impostor is not the ambiguous one.
		ambiguous:
			!verified && !impostorOf && !!asset.name && ambiguousSet.has(asset.name.trim().toLowerCase())
	}
}

/// The marks themselves. Each carries its meaning in an accessible name as
/// well as in colour, because a warning that only exists as a hue is not one.
export default function AssetBadges({
	flags,
	name,
	size = 'sm'
}: {
	flags: NameFlags
	/// The claimed name, for the impostor label ("Not the real frBTC").
	name: string | null
	size?: 'sm' | 'md'
}) {
	const box = size === 'md' ? 'h-4 w-4' : 'h-3.5 w-3.5'
	if (flags.verified) {
		return (
			<BadgeCheck
				className={cn(box, 'shrink-0 text-primary')}
				role="img"
				aria-label="Verified asset"
			>
				<title>Verified asset</title>
			</BadgeCheck>
		)
	}
	if (flags.impostorOf) {
		return (
			<AlertTriangle
				className={cn(box, 'shrink-0 text-destructive-ink')}
				role="img"
				aria-label={`Not the real ${name ?? 'asset'}`}
			>
				<title>{`Not the real ${name ?? 'asset'}`}</title>
			</AlertTriangle>
		)
	}
	if (flags.ambiguous) {
		return (
			<AlertTriangle
				className={cn(box, 'shrink-0 text-warning-ink')}
				role="img"
				aria-label="Several tokens share this name — check the id and liquidity"
			>
				<title>Several tokens share this name — check the id and liquidity</title>
			</AlertTriangle>
		)
	}
	return null
}
